/**
 * Partner E-Verify case deep link — `?pt=` → the everifyCaseQueueId it points at (VTK-1918 family).
 *
 * The partner API hands the employer a link of the form `/everifycase?pt=<JWT>`. The token carries
 * the E-Verify queue id and nothing else useful; this reads it out so the app can forward to the
 * real case screen at `/e-verify/case/:everifyCaseQueueId`.
 *
 * **The token is a pointer, not a credential.** It is decoded, never verified — client-side
 * signature checking proves nothing, and no authority is derived from it. Reaching the case screen
 * still requires an authenticated session and `eVerify.read`, enforced by the destination's own
 * route guard exactly as it is for a user who navigated from the queue. A forged or tampered token
 * therefore buys nothing: at best it names a case its holder still may not open.
 *
 * The id is the `everify_case_queue` row's id — not the DHS case number, not `everify_case` — which
 * is what both the case screen and `GET /e-verify/case-summary/:id` are addressed by.
 */

/** Base64url → JSON, without pulling in a JWT library for what is a plain payload read. */
function decodeSegment(segment: string): unknown {
  const padded = segment.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(padded + '='.repeat((4 - (padded.length % 4)) % 4));
  // atob yields one char per byte; decode those bytes as UTF-8 rather than latin1.
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return JSON.parse(new TextDecoder().decode(bytes));
}

/** A GUID, so a malformed id can never be pasted into a route path. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The E-Verify queue id the link points at, or `undefined` when the token is absent, malformed, not
 * an E-Verify case link, or carries no usable id — in which case the caller shows today's
 * unauthorized screen rather than routing somewhere arbitrary.
 */
export function everifyCaseQueueIdFromPartnerToken(rawToken?: string | null): string | undefined {
  const token = rawToken?.trim();
  if (!token) return undefined;

  const segments = token.split('.');
  if (segments.length !== 3) return undefined;

  try {
    const payload = decodeSegment(segments[1]) as {
      everifyCaseQueueId?: unknown;
      purpose?: unknown;
    };
    // The three partner audiences are deliberately distinct; the purpose check is what keeps a
    // Section 2 or candidate-prefill token from being replayed into this route. It is a sanity
    // check on our own link format, not a security boundary — the route guard is that.
    if (payload.purpose !== 'partner-everify-case') return undefined;
    const id =
      typeof payload.everifyCaseQueueId === 'string' ? payload.everifyCaseQueueId.trim() : '';
    return UUID.test(id) ? id : undefined;
  } catch {
    return undefined;
  }
}
